import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackError } from '@/lib/error-tracking';
import { useState } from 'react';

interface PayLinkCardProps {
  url: string;
  // True when the link was made by the stand-in payment account, not a real one.
  demo: boolean;
}

const COPIED_FOR_MS = 2000;

// The page a customer pays on: its address, a way to copy it, and a way to open it.
const PayLinkCard = ({ url, demo }: PayLinkCardProps) => {
  const [copied, setCopied] = useState(false);
  const title = demo ? 'Demo pay page' : 'Pay link';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_FOR_MS);
    } catch (error) {
      trackError('Could not copy the pay link', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{title}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="pay-link">Address</Label>
          <Input
            id="pay-link"
            value={url}
            readOnly
            onFocus={(event) => event.target.select()}
            className="text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href={url} target="_blank" rel="noreferrer">
              Open
            </a>
          </Button>
          <span className="sr-only" role="status">
            {copied ? 'Pay link copied' : ''}
          </span>
        </div>
        {demo ? (
          <p className="text-muted-foreground text-xs">Paying here is simulated. No money moves.</p>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default PayLinkCard;
